<template>
  <div v-if="showWholeComponent" class="demandFunnel" :class="{'pdf': this.pdf, 'uncentered': this.uncentered}">
    <Section :title="computedTitle" :subtitle="computedSubtitle" iconClass="funnel-icon" colClass="inner col-24" :dividerline="!(this.pdf)">
      <template slot="icon" v-if="!(this.pdf)">
        <FunnelIcon />
      </template>
      <template slot="content">
        <b-container fluid>
          <b-row>
            <VGraphLoader v-if="isLoading" class="col-24"/>
            <b-col v-if="!isLoading && !showFunnelShape" cols="24">
              <!-- This is the "strip" version which only shows if we have (0 < x < 4) non-zero data points -->
              <b-row class="pt-4 pb-3 px-3">
                <b-col cols="24" sm v-for="(datum, index) in stripDatapoints_filtered" :key="index">
                  <div class="text-center mx-sm-auto py-3">
                    <h1 class="bignumber">{{datum | formatNumber}}</h1>
                    <hr class="strip_hr mx-auto"/>
                    <p>{{ funnelCaption(Number(index)+1) }}</p>
                  </div>
                </b-col>
              </b-row>
            </b-col>
            <div v-if="!isLoading && showFunnelShape" class="fancy_funnel d-none d-sm-block px-0"
                   :class="{'pdf_funnel': this.pdf, 'mx-auto': !this.pdf && !this.uncentered}">
              <div class="tiers">
                <div v-for="i in 4" :key="i" class="tier" :class="['tier-'+i]">
                  <div class="svg-container">
                    <span class="svg-span">
                      <component :is="'FunnelLevel'+(5-i)" alt="" class="funnel-svg"/>
                      <div class="desc">
                        <p>{{ funnelCaption(i) }}</p>
                      </div>
                    </span>
                    <div class="totals">{{ funnel[i-1] | formatNumber }}</div>
                  </div>
                </div>
              </div>
            </div>
            <b-col v-if="!isLoading && showFunnelShape" cols="24" class="d-sm-none px-0"> <!-- Mobile Version -->
              <b-row class="funnel-container mini-funnel">
                <b-col cols="24">
                  <b-row v-for="i in 4" :key="i" class="funnel-row">
                    <b-col cols="24" class="funnel-image">
                      <b-col :cols="28-(4*i)" class="tier-image">
                        <div class="funnel-details">
                          <p class="funnel-tier-description">{{ funnelCaption(i) }}</p>
                          <p class="funnel-tier-value">{{ funnel[i-1] | formatNumber }}</p>
                        </div>
                      </b-col>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-container>
      </template>
    </Section>
  </div>
</template>

<script>
import Section from '@/components/shared/Section'

import FunnelIcon from '@/../public/svg/Funnel.svg'
import FunnelLevel1 from '@/../public/svg/funnel-level-1.svg'
import FunnelLevel2 from '@/../public/svg/funnel-level-2.svg'
import FunnelLevel3 from '@/../public/svg/funnel-level-3.svg'
import FunnelLevel4 from '@/../public/svg/funnel-level-4.svg'

import {mapGetters, mapState} from 'vuex'

export default {
  name: 'demand-funnel',
  components: {
    Section,
    FunnelIcon,
    FunnelLevel1,
    FunnelLevel2,
    FunnelLevel3,
    FunnelLevel4
  },
  computed: {
    ...mapGetters('buyers', [
      'funnel',
      'loadingFunnel'
    ]),
    ...mapGetters('listings', [
      'address',
      'beds',
    ]),
    ...mapState('listings',[
      'loadingListing'
    ]),
    ...mapGetters('config', [
      'funnelContent',
      'loadingConfig'
    ]),
    ...mapState('config', ['config']),
    isLoading () {
      return this.loadingListing || this.loadingFunnel || this.loadingConfig
    },
    hasData() {
      return (
        Array.isArray(this.funnel) &&
        this.funnel.length >= 4 &&
        this.funnel.reduce((a,i)=>a+i,0) > 0 && //has at least one non-zero item
        [1,2,3,4].every(i => this.funnelText(i).length > 0) //all the captions have been provided in config
      )
    },
    funnelText() {
      return level => {
        switch(level) {
          case 1: return this.level_1_text || this.funnelContent(1);
          case 2: return this.level_2_text || this.funnelContent(2);
          case 3: return this.level_3_text || this.funnelContent(3);
          case 4: return this.level_4_text || this.funnelContent(4);
          default: return null;
        }
      }
    },
    showWholeComponent () {
      return this.isLoading || this.hasData
    },
    showFunnelShape () {
      // Only show the funnel shape if we have all 4 levels
      // If we have 1 to 3 levels only, show the "strip" version of these datapoints instead
      return Array.isArray(this.funnel) &&
        this.funnel.length >= 4 &&
        this.funnel.every( v => v > 0 )
    },
    stripDatapoints_filtered() {
      let obj = {};
      this.funnel.map( (value,idx) => {
        if(value > 0)
          obj[idx] = value;
      })
      return obj;
    },
    computedTitle() {
      return this.title || this.$options.filters.replaceWLCKeywords(this.config.funnel_header_text) || "Buyer Activity"
    },
    computedSubtitle() {
      return this.subtitle || this.$options.filters.replaceWLCKeywords(this.config.funnel_sub_text) || "These statistics reflect all buyer activity including: registered buyers, online inquiries, showing visits, open house sign ins, etc."
    }
  },
  methods : {
    replaceAddress (rawString) {
      if(typeof rawString === 'string') {
        return rawString.replace("{address}", this.address)
      }
      return rawString
    },
    replaceBedroom (rawString) {
      if(typeof rawString === 'string' && this.beds) {
        return rawString.replace("#", this.beds)
      }
      return rawString
    },
    funnelCaption(index) {
      switch(index) {
        case 2:   return this.replaceAddress(this.funnelText(2))
        case 4:   return this.replaceBedroom(this.funnelText(4))
        default:  return this.funnelText(index) || ''
      }
    }
  },
  watch: {
    showWholeComponent () {
      this.$emit('update:isVisible', this.showWholeComponent);
    }
  },
  props : {
    title: {
      type: String,
      required: false
    },
    subtitle: {
      type: String,
      default: "These statistics reflect all buyer activity including: registered buyers, online inquiries, showing visits, open house sign ins, etc."
    },
    pdf: { //generate the PDF version
      type: Boolean,
      default: false,
    },

    level_1_text: {
      type: String,
      required: false
    },
    level_2_text: {
      type: String,
      required: false
    },
    level_3_text: {
      type: String,
      required: false
    },
    level_4_text: {
      type: String,
      required: false
    },

    uncentered: {
      type: Boolean,
      default: false
    }

  }
}
</script>

<style lang="scss">
@import "~bootstrap/scss/_functions.scss";
@import '@/styles/_custom-variables.scss';
@import '@/styles/_custom-mixins.scss';

.fancy_funnel {
  flex: 0 0 700px;
  padding-bottom: 50px;

  @at-root .demandFunnel.uncentered & {
    margin-left: calc(50vw - 450px);
  }
}

.strip_hr {
  max-width: 150px;
}

$funnel-widths-md: (
  tier-1: 480px,
  tier-2: 380px,
  tier-3: 280px,
  tier-4: 178.5px
);
$funnel-height-md: 87px;

$funnel-widths-lg: (
  tier-1: 641px,
  tier-2: 507px,
  tier-3: 373px,
  tier-4: 238px
);
$funnel-height-lg: 116px;

$funnel-widths-pdf: (
  tier-1: 925.8px,
  tier-2: 730.9px,
  tier-3: 536px,
  tier-4: 341.13px
);
$funnel-height-pdf: 169.9px;

.tiers {
  margin-top: 48px;
  .tier {
    height: $funnel-height-md;
    position: relative;

    .svg-container {
      display: inline-block;
      position: relative;
      text-align: center;
      height: $funnel-height-md;
      width: map-get($funnel-widths-md,"tier-1");

      svg {
        z-index: -1;
        top: 0;
        height: auto;
        margin-top: 0;

        .dropshadow {
          fill: $dropshadow;
        }
        .people_group {
          opacity: 0.1;
        }
        .person {
          fill: #fff;
        }
        .circle_bg {
          fill: #fff;
        }
        .trapezoid, .circle {
          fill: $primary;
        }
      }

      .svg-span{
        display: inline-block;
        position: relative;
      }
      .svg-span::after {
        content: '';
        width: 75px;
        height: 1px;
        position: absolute;
        right: -54px;
        top: calc(#{$funnel-height-md} * 0.48);
        background-color: $line_gray;
      }

      .totals {
        position: absolute;
        @include centerThis(absolute);
        top: calc(#{$funnel-height-md} * 0.3);
        display: inline-block;
        color: $primaryContrastFontColor;
        font-size: 20px;
        font-weight: 800;
      }

      .desc {
        position: absolute;
        top: 0;
        right: 0;
        vertical-align: top;
        display: inline-block;
        text-align: left;

        p {
          margin-top: calc(#{$funnel-height-md} / 3);
          position: relative;
          display: inline-block;
          width: 146px;
          vertical-align: middle;
        }
      }
    }
    &.tier-1{
      .desc {
        left: calc(#{map-get($funnel-widths-md,"tier-1")} + 75px);
      }
      svg {
        width: map-get($funnel-widths-md,"tier-1");
        .trapezoid {
          fill: _lightness($primary, 1.5);
        }
      }
    }
    &.tier-2 {
      .desc {
        left: calc(#{map-get($funnel-widths-md,"tier-2")} + 75px);
      }
      svg {
        width: map-get($funnel-widths-md,"tier-2");
        .trapezoid {
          fill: _lightness($primary, 1.25);
        }
      }
    }
    &.tier-3 {
      .desc {
        left: calc(#{map-get($funnel-widths-md,"tier-3")} + 75px);
      }
      svg {
        width: map-get($funnel-widths-md,"tier-3");
        .trapezoid {
          fill: $primary;
        }
      }
    }
    &.tier-4 {
      .desc {
        left: calc(#{map-get($funnel-widths-md,"tier-4")} + 75px);
      }
      svg {
        width: map-get($funnel-widths-md,"tier-4");
        height: auto;
        margin-top: 0;
        .trapezoid {
          fill: _lightness($primary, .75);
        }
      }
      .svg-span::after {
        top: calc(#{map-get($funnel-widths-md,"tier-4")} * .4269 * 0.55);
      }
    }
  }
}


@media only screen and (min-width: $breakpoint-md) {

  .fancy_funnel {
    flex: 0 0 900px;

    @at-root .demandFunnel.uncentered & {
      margin-left: calc(50vw - 575px);
    }
  }

  .tiers {
    .tier {
      height: $funnel-height-lg;

      .svg-container {
        height: $funnel-height-lg;
        width: map-get($funnel-widths-lg,"tier-1");

        .svg-span::after {
          width: 75px;
          right: -50px;
          top: calc(#{$funnel-height-lg} * 0.48);
        }

        .desc {
          p {
            margin-top: calc(#{$funnel-height-lg} / 3);
            width: 190px;
          }
        }

        .totals {
          top: calc(#{$funnel-height-lg} / 3);
        }
      }
      &.tier-1{
        .desc {
          left: calc(#{map-get($funnel-widths-lg,"tier-1")} + 75px);
        }
        svg {
          width: map-get($funnel-widths-lg,"tier-1");
        }
      }
      &.tier-2 {
        .desc {
          left: calc(#{map-get($funnel-widths-lg,"tier-2")} + 75px);
        }
        svg {
          width: map-get($funnel-widths-lg,"tier-2");
        }
      }
      &.tier-3 {
        .desc {
          left: calc(#{map-get($funnel-widths-lg,"tier-3")} + 75px);
        }
        svg {
          width: map-get($funnel-widths-lg,"tier-3");
        }
      }
      &.tier-4 {
        .desc {
          left: calc(#{map-get($funnel-widths-lg,"tier-4")} + 75px);
        }
        svg {
          width: map-get($funnel-widths-lg,"tier-4");
        }
        .svg-span::after {
          top: calc(#{map-get($funnel-widths-lg,"tier-4")} * .4269 * 0.55);
        }
      }
    }
  }
}


@media only screen and (min-width: $breakpoint-lg) {

  .fancy_funnel {
      flex: 0 0 1000px;

      @at-root .demandFunnel.uncentered & {
        margin-left: calc(50vw - 675px);
      }
    }
  .tiers .tier .svg-container .desc {
    p {
      width: 280px;
    }
  }
}

//specifically for pdf funnel
.demandFunnel.pdf {

  .fancy_funnel.pdf_funnel {
    //flex: 0 0 900px;
    padding-bottom: 30px;

    .tiers {
      margin-top: 38px;
      .tier {
        height: $funnel-height-pdf;

        .svg-container {
          height: $funnel-height-pdf;
          width: map-get($funnel-widths-pdf,"tier-1");

          svg {
            .people_group {
              opacity: 0.2;
            }
          }

          .svg-span::after {
            width: 90px;
            height: 2px;
            right: -55px;
            top: calc(#{$funnel-height-pdf} * 0.48);
          }

          .desc {
            p {
              margin-top: calc(#{$funnel-height-pdf} / 3);
              margin-left: 25px;
              width: 410px;
              font-size: 22px;
              font-weight: 600;
              line-height: 25.6px;
            }
          }

          .totals {
            top: calc((#{$funnel-height-pdf} / 3) - 18px);
            font-size: 44px;
          }
        }
        &.tier-1{
          .desc {
            left: calc(#{map-get($funnel-widths-pdf,"tier-1")} + 75px);
          }
          svg {
            width: map-get($funnel-widths-pdf,"tier-1");
            .trapezoid {
              fill: scale-color(#181818, $lightness: 80%);
            }
          }
        }
        &.tier-2 {
          .desc {
            left: calc(#{map-get($funnel-widths-pdf,"tier-2")} + 75px);
          }
          svg {
            width: map-get($funnel-widths-pdf,"tier-2");
            .trapezoid {
              fill: scale-color(#181818, $lightness: 60%);
            }
          }
        }
        &.tier-3 {
          .desc {
            left: calc(#{map-get($funnel-widths-pdf,"tier-3")} + 75px);
          }
          svg {
            width: map-get($funnel-widths-pdf,"tier-3");
            .trapezoid {
              fill: scale-color(#181818, $lightness: 40%);
            }
          }
        }
        &.tier-4 {
          .desc {
            left: calc(#{map-get($funnel-widths-pdf,"tier-4")} + 75px);
          }
          svg {
            width: map-get($funnel-widths-pdf,"tier-4");
            .trapezoid {
              fill: #181818;
            }
          }
          .svg-span::after {
            top: calc(#{map-get($funnel-widths-pdf,"tier-4")} * .4269 * 0.55);
          }
        }
      }
    }
  }
}

// this is for the tiny viewport version
.funnel-container.mini-funnel {
  // margin-top:20px;
  // margin-bottom: 20px;

  .tier-image {
    min-height: 120px;
    height: 100%;
    width: 100%;
  }
  .funnel-row {
    .funnel-details {
      margin-left: 6vw;
      padding-top: 20px;
      padding-bottom: 1px;
      .funnel-tier-value {
        line-height: normal;
        font-size: 36px;
        font-weight: 300;
        color: $primaryContrastFontColor;
      }
      .funnel-tier-description {
        line-height: 18px;
        font-size: 16px;
        color: $primaryContrastFontColor;
        margin-right: 10%;
      }
    }
    &:nth-child(1) {
      .tier-image {
        background-color: _lightness($primary, 1.5);
      }
    }
    &:nth-child(2) {
      .tier-image {
        background-color: _lightness($primary, 1.25);
      }
    }
    &:nth-child(3) {
      .tier-image {
        background-color: $primary;
      }
    }
    &:nth-child(4) {
      .tier-image {
        background-color: _lightness($primary, 0.75);
      }
    }
  }
}

</style>
